<template>
  <component
    :is="link ? UiLink : 'article'"
    class="relative inline-block max-w-lg overflow-hidden rounded-lg bg-white"
    :to="link"
  >
    <div
      v-if="isNew"
      class="absolute left-2 top-2 z-10 flex h-5 items-center justify-center rounded-full bg-azure px-[6px] text-sm capitalize text-white md:left-4 md:top-4 md:h-7 md:px-2.5"
    >
      {{ $t('new') }}
    </div>

    <div class="relative text-slate">
      <div class="relative aspect-[156/130] bg-white">
        <CdnImg
          v-if="image"
          :src="handleImageSrc(image)"
          :alt="handleImageAlt(image)"
          :width="449"
          :height="449"
          class="h-full w-full object-contain"
          loading="lazy"
        />

        <div v-else class="bg-gray-light absolute inset-0"></div>
      </div>

      <div class="px-4 py-4 sm:px-6 sm:text-center md:py-8">
        <h3
          class="title text-lg uppercase !leading-none sm:break-words xl:text-xl"
          v-html="name"
        />

        <p
          v-if="description"
          class="mt-2 text-[0.75rem] leading-4 sm:text-sm sm:leading-[1.225rem]"
          v-html="description"
        />
        <div
          v-if="tags.length"
          class="mt-6 hidden flex-wrap justify-center gap-1 md:flex"
        >
          <UiTag
            v-for="(tag, i) in tags"
            :key="i"
            uppercase
            size="md"
            color="gray"
          >
            <span v-html="tag" />
          </UiTag>
        </div>
      </div>

      <UiEnergyLabelLink
        v-if="energyLabelLink && energyLabelSymbol"
        :link-html="energyLabelLink"
        :energy-efficiency-class="energyLabelSymbol"
        height="27"
        class="px-4 pb-4 sm:px-6 md:pb-8"
      />
    </div>
  </component>
</template>

<script setup lang="ts">
import { CdnImg } from '@webplatform/cdn-image';
import { handleImageSrc, handleImageAlt } from '~/helpers';
import UiEnergyLabelLink from '~/components/ui/UiEnergyLabelLink.vue';
import UiLink from '~/components/ui/UiLink.vue';
import { computed } from 'vue';
import { getProductFeatureValueById } from '~/helpers/vue';

interface Props {
  image?: any;
  name?: string;
  description?: string;
  isNew?: boolean;
  link?: string;
  tags?: any[];
  featureGroups?: any[];
}

const props = withDefaults(defineProps<Props>(), {
  image: () => ({}),
  name: '',
  description: '',
  isNew: false,
  link: '',
  tags: () => [],
  featureGroups: () => [],
});

const energyLabelLink = computed(() =>
  getProductFeatureValueById(
    'ADDITIONAL_INFORMATION',
    'EPREL',
    props.featureGroups,
  ),
);

const energyLabelSymbol = computed(() =>
  getProductFeatureValueById(
    'ADDITIONAL_INFORMATION',
    'ENERGY_LABEL',
    props.featureGroups,
  ),
);
</script>
